import React, { useState, useRef, useEffect } from 'react';
import './Blog.css';
import { scrollToSection } from '../utils/scroll';

function Blog() {
  // LinkedIn embedded posts
  const linkedInPosts = [
    {
      id: 1,
      embedCode: '<iframe src="https://www.linkedin.com/embed/feed/update/urn:li:share:7324125759076188160" height="916" width="504" frameborder="0" allowfullscreen="" title="Embedded post"></iframe>'
    },
    {
      id: 2,
      embedCode: '<iframe src="https://www.linkedin.com/embed/feed/update/urn:li:share:7321894251242442752" height="1020" width="504" frameborder="0" allowfullscreen="" title="Embedded post"></iframe>'
    },
    {
      id: 3,
      embedCode: '<iframe src="https://www.linkedin.com/embed/feed/update/urn:li:share:7321342486881177601" height="948" width="504" frameborder="0" allowfullscreen="" title="Embedded post"></iframe>'
    },
    {
      id: 4,
      embedCode: '<iframe src="https://www.linkedin.com/embed/feed/update/urn:li:share:7319761813011664896" height="755" width="504" frameborder="0" allowfullscreen="" title="Embedded post"></iframe>'
    },
    {
      id: 5,
      embedCode: '<iframe src="https://www.linkedin.com/embed/feed/update/urn:li:share:7317960313927053312" height="1538" width="504" frameborder="0" allowfullscreen="" title="Embedded post"></iframe>'
    },
    {
      id: 6,
            embedCode: '<iframe src="https://www.linkedin.com/embed/feed/update/urn:li:ugcPost:7317612774912589824" height="1116" width="504" frameborder="0" allowfullscreen="" title="Embedded post"></iframe>'
    }
  ];

  const carouselRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const linkedInProfileUrl = "https://www.linkedin.com/in/amirali86";

  // Function to navigate to the previous slide
  const goToPrevSlide = () => {
    setCurrentSlide(prev => (prev === 0 ? linkedInPosts.length - 1 : prev - 1));
    // Pause auto-rotation temporarily when user manually navigates
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 10000); // Resume after 10 seconds
  };

  // Function to navigate to the next slide
  const goToNextSlide = () => {
    setCurrentSlide(prev => (prev === linkedInPosts.length - 1 ? 0 : prev + 1));
    // Pause auto-rotation temporarily when user manually navigates
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 10000); // Resume after 10 seconds
  };

  // Function to create the HTML markup for the embed code
  const createMarkup = (htmlContent) => {
    return { __html: htmlContent };
  };

  // Auto-rotate slides every 5 seconds
  useEffect(() => {
    const autoRotate = setInterval(() => {
      if (!isPaused) {
        setCurrentSlide(prev => (prev === linkedInPosts.length - 1 ? 0 : prev + 1));
      }
    }, 5000);

    // Clear interval on component unmount
    return () => clearInterval(autoRotate);
  }, [isPaused, linkedInPosts.length]);

  // Effect to adjust the height of the carousel container based on current slide
  useEffect(() => {
    if (carouselRef.current) {
      // Set a fixed height for the carousel container or adjust based on content
      carouselRef.current.style.height = '800px'; // Adjust this value as needed
    }
  }, [currentSlide]);

  return (
    <section id="blog" className="blog">
      <div className="blog-container">
        <h2>Insights & Articles</h2>
        <p className="blog-intro">
          Thought leadership and industry insights on impact finance, community development, and innovative financing solutions.
        </p>
        
        {/* LinkedIn Posts Carousel Section */}
        <div className="linkedin-posts-section">
          <div className="linkedin-header">
            <div className="section-title-container">
              <h3>From Our LinkedIn</h3>
              <div className="section-divider"></div>
            </div>
            <a 
              href={linkedInProfileUrl} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="view-all-link"
            >
              View All on LinkedIn
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: '6px' }}>
                <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/>
              </svg>
            </a>
          </div>
          
          <div className="carousel-container">
            <div className="carousel-navigation">
              <button 
                onClick={goToPrevSlide} 
                className="carousel-btn prev-btn"
                aria-label="Previous post"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.5 19L8.5 12L15.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <div className="carousel-indicators">
                {linkedInPosts.map((_, index) => (
                  <button 
                    key={index} 
                    className={`carousel-indicator ${index === currentSlide ? 'active' : ''}`}
                    onClick={() => {
                      setCurrentSlide(index);
                      setIsPaused(true);
                      setTimeout(() => setIsPaused(false), 10000);
                    }}
                    aria-label={`Go to slide ${index + 1}`}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
              <button 
                onClick={goToNextSlide} 
                className="carousel-btn next-btn"
                aria-label="Next post"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.5 5L15.5 12L8.5 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            
            <div 
              className="carousel-track" 
              ref={carouselRef}
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              {linkedInPosts.map((post, index) => (
                <div 
                  key={post.id} 
                  className={`carousel-slide ${index === currentSlide ? 'active' : ''}`}
                  style={{ display: index === currentSlide ? 'block' : 'none' }}
                >
                  <div className="linkedin-post-container" dangerouslySetInnerHTML={createMarkup(post.embedCode)} />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="services-cta">
          <h3>Ready to Create Innovative Solutions?</h3>
          <p>
            Our team has extensive experience in structuring complex financing for impactful community development projects.
            Let's discuss how we can help your organization achieve its mission.
          </p>
          <button 
            className="contact-btn"
            onClick={() => scrollToSection('contact')}
          >
            Contact Us Today
          </button>
        </div>
      </div>
    </section>
  );
}

export default Blog; 